import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useClerk } from "@clerk/clerk-react";
import { RotateCcwIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import {
	isChatModelId,
	MODEL_OPTIONS,
	type ChatModelId,
} from "./chat-model.js";
import { clerkErrorMessage } from "./clerk-errors.js";

/** Local Vite, or the desk shipped with Clerk Development (`pk_test`). */
export function accountResetAvailable(): boolean {
	return (
		import.meta.env.DEV ||
		import.meta.env.VITE_CLERK_INSTANCE === "development"
	);
}

const CLIENT_KEYS = [
	"mairko-matrix-onboarding",
	"mairko-matrix-chat-threads",
	"mairko-matrix-desk",
	"mairko-matrix-place",
] as const;

function clearMatrixClientState(): void {
	for (const key of CLIENT_KEYS) {
		try {
			localStorage.removeItem(key);
		} catch {
			/* private mode */
		}
	}
}

async function resetDevAccount(): Promise<
	{ ok: true } | { ok: false; error: string }
> {
	try {
		const response = await fetch("/api/dev/reset", {
			method: "POST",
			credentials: "include",
		});
		if (response.status === 404) {
			return { ok: false, error: "Account reset is off on this instance." };
		}
		if (response.status === 403) {
			return {
				ok: false,
				error: "Won’t wipe the seed tenant. Use a personal sign-in.",
			};
		}
		if (!response.ok) {
			return { ok: false, error: `Reset failed (${response.status}).` };
		}
		return { ok: true };
	} catch {
		return { ok: false, error: "Could not reach the platform." };
	}
}

/** Dialog must sit outside DropdownMenu — the menu unmounts on close. */
export function useDevReset(): {
	openReset: () => void;
	dialog: ReactNode;
} {
	const [open, setOpen] = useState(false);
	if (!accountResetAvailable()) {
		return { openReset: () => {}, dialog: null };
	}
	return {
		openReset: () => setOpen(true),
		dialog: <DevResetDialog open={open} onOpenChange={setOpen} />,
	};
}

function DevResetDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { signOut } = useClerk();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const run = async () => {
		setBusy(true);
		setError(null);
		const result = await resetDevAccount();
		if (!result.ok) {
			setError(result.error);
			setBusy(false);
			return;
		}
		clearMatrixClientState();
		try {
			await signOut({ redirectUrl: "/" });
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
			setBusy(false);
			window.location.assign("/");
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (busy) return;
				setError(null);
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md" showCloseButton>
				<DialogHeader>
					<DialogTitle>Reset this account?</DialogTitle>
					<DialogDescription>
						Deletes the workspace, Clerk user, and local Matrix state.
						You’ll land on sign-in.
					</DialogDescription>
				</DialogHeader>
				{error && (
					<p className="settings-form-error m-0" role="alert">
						{error}
					</p>
				)}
				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						disabled={busy}
						onClick={() => onOpenChange(false)}
					>
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						disabled={busy}
						onClick={() => void run()}
					>
						{busy ? "Resetting…" : "Wipe and restart"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

type TriggerProps = {
	variant: "bar" | "menu";
	onClick: () => void;
};

export function DevResetTrigger({ variant, onClick }: TriggerProps) {
	if (!accountResetAvailable()) return null;
	if (variant === "menu") {
		return (
			<DropdownMenuItem onSelect={onClick}>
				<RotateCcwIcon className="size-4" />
				Reset account
			</DropdownMenuItem>
		);
	}
	return (
		<button type="button" className="mc-ob-reset" onClick={onClick}>
			<RotateCcwIcon className="size-3.5" aria-hidden />
			Reset
		</button>
	);
}

/** Dev-only model switch — lives next to Reset, not in the composer. */
export function DevModelSelect({
	value,
	onChange,
}: {
	value: ChatModelId;
	onChange: (modelId: ChatModelId) => void;
}) {
	if (!import.meta.env.DEV) return null;
	return (
		<label className="mc-dev-model">
			<span className="sr-only">Chat model</span>
			<select
				className="mc-dev-model-select"
				value={value}
				onChange={(event) => {
					const next = event.target.value;
					if (isChatModelId(next)) onChange(next);
				}}
			>
				{MODEL_OPTIONS.map((opt) => (
					<option key={opt.id} value={opt.id}>
						{opt.label}
					</option>
				))}
			</select>
		</label>
	);
}
